// client/src/hooks/useItensVendaveis.js - VERSÃO 100% CORRIGIDA E FUNCIONAL
import { useItensVenda } from './useItensVenda';
import { useServicos } from './useServicos';
import { useRecursos } from './useRecursos';
import { useOfertas } from './useOfertas';
import { usePacotesCliente } from './usePacotesCliente';
import { useMemo, useEffect } from 'react';

// ✅ FUNÇÃO AUXILIAR MELHORADA: Para extrair dados de forma segura
const safeQueryData = (data, defaultValue = []) => {
  if (!data) return defaultValue;
  
  // Se já for array, retorna diretamente
  if (Array.isArray(data)) return data;
  
  // Se for objeto com propriedade data
  if (data.data && Array.isArray(data.data)) return data.data;
  
  // Se for objeto com propriedade itens
  if (data.itens && Array.isArray(data.itens)) return data.itens;
  
  // Se for objeto com outras propriedades de array
  const arrayProps = Object.values(data).find(val => Array.isArray(val));
  return arrayProps || defaultValue;
};

// ✅ FUNÇÃO: Processar ItemVenda (Produtos físicos) - CORRIGIDA
const processarItemVenda = (item) => {
  if (!item || typeof item !== 'object' || !item._id) {
    console.warn('⚠️ [processarItemVenda] Item inválido ignorado:', item);
    return null;
  }

  try {
    // ✅ CORREÇÃO: Calcular preço base considerando TODAS as unidades
    const unidadesAtivas = Array.isArray(item.unidadesDetalhes) 
      ? item.unidadesDetalhes.filter(ud => ud.ativo && ud.disponivel)
      : [];
    
    const precos = unidadesAtivas.map(ud => Number(ud.preco) || 0);
    const precoBase = precos.length > 0 ? Math.min(...precos) : 0;
    
    // ✅ CORREÇÃO: Calcular estoque total considerando TODAS as unidades
    const estoqueTotal = unidadesAtivas.reduce((acc, ud) => 
      acc + (Number(ud.quantidadeEmEstoque) || 0), 0
    );
    
    // ✅ CORREÇÃO: Verificar disponibilidade considerando controle de estoque
    const disponivel = item.controlaEstoque ? estoqueTotal > 0 : unidadesAtivas.length > 0;
    const itemAtivo = item.ativo !== false && item.visivelParaCliente !== false;

    return {
      id: item._id,
      tipo: 'produto',
      modelo: 'ItemVenda',
      nome: item.nome || 'Produto sem nome',
      descricao: item.descricao || '',
      preco: precoBase,
      precoBase: precoBase,
      imagemUrl: item.imagemUrl || item.getImagemFallback?.() || '/images/default-product.png',
      sku: item.sku || '',
      controlaEstoque: Boolean(item.controlaEstoque),
      estoque: estoqueTotal,
      disponivel: disponivel && itemAtivo,
      categoria: 'Produto',
      metadata: {
        unidades: unidadesAtivas,
        unidadePadrao: unidadesAtivas[0]?.unidade || null,
        categoria: item.categoria,
        tags: item.tags || [],
        tipoItem: item.metadata?.tipoItem || 'fisico'
      },
      // ✅ NOVO: Dados originais para referência
      _original: item
    };
  } catch (error) {
    console.error('❌ [processarItemVenda] Erro ao processar item:', error, item);
    return null;
  }
};

// ✅ FUNÇÃO: Processar Servico - CORRIGIDA
const processarServico = (servico) => {
  if (!servico || typeof servico !== 'object' || !servico._id) {
    console.warn('⚠️ [processarServico] Serviço inválido ignorado:', servico);
    return null;
  }

  try {
    // ✅ CORREÇÃO: Calcular preço considerando unidades
    const unidadesAtivas = Array.isArray(servico.unidadesDetalhes) 
      ? servico.unidadesDetalhes.filter(ud => ud.ativo && ud.disponivel)
      : [];
    
    const precos = unidadesAtivas.map(ud => Number(ud.preco) || 0);
    const precoBase = precos.length > 0 ? Math.min(...precos) : 
                     Number(servico.valorFixo) || Number(servico.valorPorHora) || 0;

    return {
      id: servico._id,
      tipo: 'servico',
      modelo: 'Servico',
      nome: servico.nome || 'Serviço sem nome',
      descricao: servico.descricao || '',
      preco: precoBase,
      precoBase: precoBase,
      imagemUrl: servico.imagemUrl || '/images/default-service.png',
      duracaoMinutos: Number(servico.duracaoPadrao) || 60,
      requerAgendamento: true,
      disponivel: servico.ativo !== false && servico.visivelParaCliente !== false,
      categoria: 'Serviço',
      metadata: {
        profissionais: Array.isArray(servico.profissionais) ? servico.profissionais : [],
        recursosNecessarios: Array.isArray(servico.recursos) ? servico.recursos : [],
        unidades: unidadesAtivas,
        unidadePadrao: unidadesAtivas[0]?.unidade || null,
        categoria: servico.categoria
      },
      _original: servico
    };
  } catch (error) {
    console.error('❌ [processarServico] Erro ao processar serviço:', error, servico);
    return null;
  }
};

// ✅ FUNÇÃO: Processar Recurso (Locações) - CORRIGIDA
const processarRecurso = (recurso) => {
  if (!recurso || typeof recurso !== 'object' || !recurso._id) {
    console.warn('⚠️ [processarRecurso] Recurso inválido ignorado:', recurso);
    return [];
  }

  try {
    const itensRecurso = [];

    // ✅ CORREÇÃO: Verificar disponibilidade do recurso
    const recursoDisponivel = recurso.status === 'Ativo' && recurso.ativo !== false;

    // Opção por hora
    if (recurso.valorPorHora > 0) {
      itensRecurso.push({
        id: `${recurso._id}_hora`,
        tipo: 'locacao',
        modelo: 'Recurso',
        nome: `${recurso.nome || 'Recurso'} (Locação por Hora)`,
        descricao: recurso.descricao || '',
        preco: Number(recurso.valorPorHora) || 0,
        precoBase: Number(recurso.valorPorHora) || 0,
        imagemUrl: recurso.imagemUrl || '/images/default-resource.png',
        unidade: 'hora',
        requerAgendamento: true,
        disponivel: recursoDisponivel,
        categoria: 'Locação',
        metadata: {
          tipoRecurso: recurso.tipo,
          precificacao: 'hora',
          unidade: recurso.unidade,
          configuracoesAgenda: recurso.configuracoesAgenda,
          recursoOriginal: recurso._id
        },
        _original: recurso
      });
    }

    // ✅ CORREÇÃO: Pacotes de preço para recursos - validação melhorada
    if (recurso.tipoDePrecificacao === 'Por Pacote' && Array.isArray(recurso.pacotesDePreco)) {
      recurso.pacotesDePreco.forEach(pacote => {
        if (pacote && typeof pacote === 'object' && pacote.ativo !== false) {
          itensRecurso.push({
            id: `${recurso._id}_pacote_${pacote._id || Date.now()}`,
            tipo: 'locacao',
            modelo: 'Recurso',
            nome: `${recurso.nome || 'Recurso'} - ${pacote.nome || 'Pacote'}`,
            descricao: `${recurso.descricao || ''} - ${pacote.descricao || ''}`,
            preco: Number(pacote.valorComDesconto) || Number(pacote.valor) || 0,
            precoBase: Number(pacote.valor) || 0,
            imagemUrl: recurso.imagemUrl || '/images/default-resource.png',
            unidade: 'pacote',
            duracaoHoras: Number(pacote.duracaoHoras) || 1,
            requerAgendamento: true,
            disponivel: recursoDisponivel && pacote.ativo !== false,
            categoria: 'Locação',
            metadata: {
              tipoRecurso: recurso.tipo,
              precificacao: 'pacote',
              pacoteId: pacote._id,
              unidade: recurso.unidade,
              recursoOriginal: recurso._id
            },
            _original: { ...recurso, pacote }
          });
        }
      });
    }

    return itensRecurso;
  } catch (error) {
    console.error('❌ [processarRecurso] Erro ao processar recurso:', error, recurso);
    return [];
  }
};

// ✅ FUNÇÃO: Processar Oferta - CORRIGIDA
const processarOferta = (oferta) => {
  if (!oferta || typeof oferta !== 'object' || !oferta._id) {
    console.warn('⚠️ [processarOferta] Oferta inválida ignorada:', oferta);
    return null;
  }

  try {
    // ✅ CORREÇÃO: Calcular preço considerando unidades
    const unidadesAtivas = Array.isArray(oferta.unidadesDetalhes) 
      ? oferta.unidadesDetalhes.filter(ud => ud.ativo && ud.disponivel)
      : [];
    
    let preco = 0;
    
    if (oferta.tipoDePrecificacao === 'Fixo') {
      const precos = unidadesAtivas.map(ud => Number(ud.preco) || 0);
      preco = precos.length > 0 ? Math.min(...precos) : Number(oferta.valorFixo) || 0;
    } else if (oferta.tipoDePrecificacao === 'Por Pacote' && Array.isArray(oferta.pacotesDePreco) && oferta.pacotesDePreco.length > 0) {
      // Usar o menor preço dos pacotes válidos
      const precosValidos = oferta.pacotesDePreco
        .filter(p => p && typeof p === 'object' && p.ativo !== false && p.valor != null)
        .map(p => Number(p.valorComDesconto) || Number(p.valor) || 0);
      preco = precosValidos.length > 0 ? Math.min(...precosValidos) : 0;
    } else {
      preco = Number(oferta.valorTotalEstimado) || 0;
    }

    return {
      id: oferta._id,
      tipo: 'oferta',
      modelo: 'Oferta',
      nome: oferta.nome || 'Oferta sem nome',
      descricao: oferta.descricao || '',
      preco: preco,
      precoBase: preco,
      imagemUrl: oferta.imagemUrl || '/images/default-offer.png',
      disponivel: oferta.ativo !== false && oferta.visivelParaCliente !== false && unidadesAtivas.length > 0,
      categoria: oferta.tipo === 'Combo' ? 'Combo' : 'Pacote',
      metadata: {
        tipoOferta: oferta.tipo,
        itens: Array.isArray(oferta.itens) ? oferta.itens : [],
        pacotesDePreco: Array.isArray(oferta.pacotesDePreco) ? oferta.pacotesDePreco : [],
        agendavel: Boolean(oferta.agendavel),
        unidades: unidadesAtivas,
        unidadePadrao: unidadesAtivas[0]?.unidade || null
      },
      _original: oferta
    };
  } catch (error) {
    console.error('❌ [processarOferta] Erro ao processar oferta:', error, oferta);
    return null;
  }
};

// ✅ FUNÇÃO: Processar PacoteCliente - CORRIGIDA
const processarPacoteCliente = (pacote) => {
  if (!pacote || typeof pacote !== 'object' || !pacote._id) {
    console.warn('⚠️ [processarPacoteCliente] Pacote cliente inválido ignorado:', pacote);
    return null;
  }

  try {
    // ✅ CORREÇÃO: Verificar se pacote pode agendar com validações completas
    const sessoesRestantes = Number(pacote.sessoesRestantes) || 0;
    const sessoesTotais = Number(pacote.pacoteComprado?.sessoes) || Number(pacote.sessoesTotais) || 0;
    
    const dataValidade = pacote.dataValidade ? new Date(pacote.dataValidade) : null;
    const hoje = new Date();
    
    const podeAgendar = pacote.status === 'Ativo' && 
                       sessoesRestantes > 0 &&
                       (!dataValidade || dataValidade > hoje);

    if (podeAgendar) {
      return {
        id: pacote._id,
        tipo: 'pacote_cliente',
        modelo: 'PacoteCliente',
        nome: `Sessão - ${pacote.pacoteComprado?.nome || pacote.oferta?.nome || 'Pacote'}`,
        descricao: `Utilização de pacote adquirido (${sessoesRestantes}/${sessoesTotais} sessões restantes)`,
        preco: 0, // Já foi pago
        precoBase: 0,
        imagemUrl: pacote.oferta?.imagemUrl || '/images/default-package.png',
        disponivel: true,
        categoria: 'Pacote Cliente',
        metadata: {
          pacoteOriginal: pacote.oferta || pacote.pacoteComprado,
          sessoesRestantes: sessoesRestantes,
          sessoesTotais: sessoesTotais,
          dataValidade: pacote.dataValidade,
          itensInclusos: Array.isArray(pacote.itensDaOferta) ? pacote.itensDaOferta : [],
          unidade: pacote.unidade
        },
        _original: pacote
      };
    }
    return null;
  } catch (error) {
    console.error('❌ [processarPacoteCliente] Erro ao processar pacote cliente:', error, pacote);
    return null;
  }
};

// ✅ HOOK PRINCIPAL CORRIGIDO: useItensVendaveis
export const useItensVendaveis = (accountId, options = {}) => {
  // ✅ CORREÇÃO: Parâmetros padrão otimizados
  const defaultParams = {
    account: accountId,
    limit: 1000,
    ...options
  };

  // ✅ CORREÇÃO: Chamar hooks com parâmetros corretos
  const itensVendaQuery = useItensVenda({
    ...defaultParams,
    ativo: true,
    visivelParaCliente: true
  });

  const servicosQuery = useServicos({
    ...defaultParams,
    ativo: true,
    visivelParaCliente: true
  });

  const recursosQuery = useRecursos({
    ...defaultParams,
    status: 'Ativo',
    ativo: true
  });

  const ofertasQuery = useOfertas({
    ...defaultParams,
    ativo: true,
    visivelParaCliente: true
  });

  const pacotesQuery = usePacotesCliente({
    ...defaultParams,
    status: 'Ativo'
  });

  // ✅ DEBUG MELHORADO: Monitorar status de todos os hooks
  useEffect(() => {
    if (itensVendaQuery.data || servicosQuery.data || recursosQuery.data) {
      console.log('🚨 [useItensVendaveis] DIAGNÓSTICO COMPLETO:');
      console.log('📦 ItensVenda:', {
        count: safeQueryData(itensVendaQuery.data).length,
        loading: itensVendaQuery.isLoading,
        error: itensVendaQuery.error,
        sample: safeQueryData(itensVendaQuery.data).slice(0, 1)
      });
      console.log('⚡ Servicos:', {
        count: safeQueryData(servicosQuery.data).length,
        loading: servicosQuery.isLoading,
        error: servicosQuery.error,
        sample: safeQueryData(servicosQuery.data).slice(0, 1)
      });
      console.log('🏢 Recursos:', {
        count: safeQueryData(recursosQuery.data).length,
        loading: recursosQuery.isLoading,
        error: recursosQuery.error,
        sample: safeQueryData(recursosQuery.data).slice(0, 1)
      });
      console.log('🎁 Ofertas:', {
        count: safeQueryData(ofertasQuery.data).length,
        loading: ofertasQuery.isLoading,
        error: ofertasQuery.error
      });
      console.log('📚 PacotesCliente:', {
        count: safeQueryData(pacotesQuery.data).length,
        loading: pacotesQuery.isLoading,
        error: pacotesQuery.error
      });
    }
  }, [
    itensVendaQuery.data, servicosQuery.data, recursosQuery.data, 
    ofertasQuery.data, pacotesQuery.data
  ]);

  // ✅ COMBINAR TODOS OS DADOS - VERSÃO CORRIGIDA
  const itensVendaveis = useMemo(() => {
    if (!accountId) {
      console.warn('⚠️ [useItensVendaveis] accountId não fornecido');
      return [];
    }

    const itens = [];

    try {
      // 1. ItensVenda (Produtos físicos)
      const itensVendaArray = safeQueryData(itensVendaQuery.data);
      console.log('🛍️ [useItensVendaveis] Processando ItensVenda:', itensVendaArray.length);
      
      itensVendaArray.forEach(item => {
        const itemProcessado = processarItemVenda(item);
        if (itemProcessado) {
          itens.push(itemProcessado);
        }
      });

      // 2. Serviços
      const servicosArray = safeQueryData(servicosQuery.data);
      console.log('⚡ [useItensVendaveis] Processando Servicos:', servicosArray.length);
      
      servicosArray.forEach(servico => {
        const servicoProcessado = processarServico(servico);
        if (servicoProcessado) {
          itens.push(servicoProcessado);
        }
      });

      // 3. Recursos (Locações)
      const recursosArray = safeQueryData(recursosQuery.data);
      console.log('🏢 [useItensVendaveis] Processando Recursos:', recursosArray.length);
      
      recursosArray.forEach(recurso => {
        const recursosProcessados = processarRecurso(recurso);
        recursosProcessados.forEach(rec => {
          itens.push(rec);
        });
      });

      // 4. Ofertas (Ignorar se houver erro 500)
      if (!ofertasQuery.error || ofertasQuery.error.response?.status !== 500) {
        const ofertasArray = safeQueryData(ofertasQuery.data);
        console.log('🎁 [useItensVendaveis] Processando Ofertas:', ofertasArray.length);
        
        ofertasArray.forEach(oferta => {
          const ofertaProcessada = processarOferta(oferta);
          if (ofertaProcessada) {
            itens.push(ofertaProcessada);
          }
        });
      } else {
        console.log('⚠️ [useItensVendaveis] Ofertas ignoradas devido a erro 500');
      }

      // 5. PacotesCliente (Ignorar se houver erro 500)
      if (!pacotesQuery.error || pacotesQuery.error.response?.status !== 500) {
        const pacotesArray = safeQueryData(pacotesQuery.data);
        console.log('📚 [useItensVendaveis] Processando PacotesCliente:', pacotesArray.length);
        
        pacotesArray.forEach(pacote => {
          const pacoteProcessado = processarPacoteCliente(pacote);
          if (pacoteProcessado) {
            itens.push(pacoteProcessado);
          }
        });
      } else {
        console.log('⚠️ [useItensVendaveis] PacotesCliente ignorados devido a erro 500');
      }

      // ✅ RESUMO FINAL
      console.log('🎉 [useItensVendaveis] PROCESSAMENTO CONCLUÍDO:');
      console.log('📊 Total de itens vendáveis:', itens.length);
      
      const categorias = {};
      itens.forEach(item => {
        categorias[item.categoria] = (categorias[item.categoria] || 0) + 1;
      });
      console.log('🏷️ Distribuição por categoria:', categorias);

    } catch (error) {
      console.error('❌ [useItensVendaveis] Erro crítico ao processar itens:', error);
    }

    return itens;
  }, [
    accountId,
    itensVendaQuery.data, servicosQuery.data, recursosQuery.data, 
    ofertasQuery.data, pacotesQuery.data,
    itensVendaQuery.error, servicosQuery.error, recursosQuery.error,
    ofertasQuery.error, pacotesQuery.error
  ]);

  // ✅ CALCULAR ESTADOS CORRETAMENTE
  const isLoading = itensVendaQuery.isLoading || servicosQuery.isLoading || 
                   recursosQuery.isLoading || ofertasQuery.isLoading || 
                   pacotesQuery.isLoading;

  const hasNonCriticalErrors = (ofertasQuery.error && ofertasQuery.error.response?.status === 500) || 
                              (pacotesQuery.error && pacotesQuery.error.response?.status === 500);
  
  const hasCriticalErrors = Boolean(
    (itensVendaQuery.error && itensVendaQuery.error.response?.status !== 500) || 
    (servicosQuery.error && servicosQuery.error.response?.status !== 500) || 
    (recursosQuery.error && recursosQuery.error.response?.status !== 500)
  );

  // ✅ EXTRAR CATEGORIAS ÚNICAS
  const categorias = useMemo(() => {
    return [...new Set(itensVendaveis.map(item => item.categoria).filter(Boolean))].sort();
  }, [itensVendaveis]);

  // ✅ EXTRAR TIPOS ÚNICOS
  const tipos = useMemo(() => {
    return [...new Set(itensVendaveis.map(item => item.tipo).filter(Boolean))].sort();
  }, [itensVendaveis]);

  return {
    // Dados principais
    data: itensVendaveis,
    isLoading,
    error: hasCriticalErrors ? (itensVendaQuery.error || servicosQuery.error || recursosQuery.error) : null,
    hasNonCriticalErrors: Boolean(hasNonCriticalErrors),
    
    // Metadados úteis
    categorias,
    tipos,
    total: itensVendaveis.length,
    
    // Dados brutos para casos específicos
    rawData: {
      itensVenda: safeQueryData(itensVendaQuery.data),
      servicos: safeQueryData(servicosQuery.data),
      recursos: safeQueryData(recursosQuery.data),
      ofertas: safeQueryData(ofertasQuery.data),
      pacotesCliente: safeQueryData(pacotesQuery.data)
    },
    
    // Status individuais para debug
    status: {
      itensVenda: { 
        loading: itensVendaQuery.isLoading, 
        error: itensVendaQuery.error, 
        count: safeQueryData(itensVendaQuery.data).length 
      },
      servicos: { 
        loading: servicosQuery.isLoading, 
        error: servicosQuery.error, 
        count: safeQueryData(servicosQuery.data).length 
      },
      recursos: { 
        loading: recursosQuery.isLoading, 
        error: recursosQuery.error, 
        count: safeQueryData(recursosQuery.data).length 
      },
      ofertas: { 
        loading: ofertasQuery.isLoading, 
        error: ofertasQuery.error, 
        count: safeQueryData(ofertasQuery.data).length 
      },
      pacotesCliente: { 
        loading: pacotesQuery.isLoading, 
        error: pacotesQuery.error, 
        count: safeQueryData(pacotesQuery.data).length 
      }
    },
    
    // Funções úteis
    refetch: () => {
      itensVendaQuery.refetch();
      servicosQuery.refetch();
      recursosQuery.refetch();
      ofertasQuery.refetch();
      pacotesQuery.refetch();
    }
  };
};

// ✅ HOOK ESPECIALIZADO PARA PDV - CORRIGIDO
export const useItensVendaveisForPDV = (accountId, options = {}) => {
  const query = useItensVendaveis(accountId, options);
  
  // Se houver erro crítico, retornar dados vazios mas funcionais
  if (query.error) {
    console.warn('⚠️ [useItensVendaveisForPDV] Erro crítico, usando fallback para PDV');
    return {
      ...query,
      data: [],
      isLoading: false,
      isFallback: true,
      error: null // Não mostrar erro ao usuário no PDV
    };
  }
  
  return {
    ...query,
    // Filtra apenas itens disponíveis para PDV
    data: query.data.filter(item => item.disponivel)
  };
};

export default useItensVendaveis;